/**
 * The seven artifacts of the "Show design work" demo: a checkout redesign told as a portfolio case.
 *
 * One beat per artifact, in the order a case study earns trust: the drop-off, what people said, the
 * three tries, the one that shipped, what it did, and the lesson. White and product-centric: the phones
 * are drawn as real screens, coral marks the problem, Basket green marks the fix, and lime is kept for
 * the one row the story is about, the delivery fee.
 */

import {
  DISPLAY,
  SANS,
  circle,
  frame,
  line,
  lines,
  path,
  rect,
  text,
  wrap,
  type DemoSlide,
  type LinesOpts,
} from './slideSvg';

const C = {
  ground: '#F6F6F3',
  ink: '#151515',
  muted: '#8C8C88',
  hairline: '#E4E4DF',
  green: '#1F7A4D',
  lime: '#D7F26B',
  coral: '#E27F6C',
  white: '#FFFFFF',
  /** Neutral bars and tracks in the charts. */
  bar: '#DCDCD6',
  track: '#ECECE7',
  /** A filled surface inside a phone: the delivery row, the order card. */
  wash: '#F3F3F0',
} as const;

const W = 1280;
const H = 720;
/** The side margin. The running line, the headline and every grid hang on it. */
const M = 64;
const R = W - M;
/** The headline's first baseline, the same on every content slide. */
const HEAD_Y = 144;
/** Where a visual under a one line headline starts, and where it must end. */
const VISUAL_Y = 200;
const VISUAL_END = 656;
/** Three columns across the content width. */
const COL_W = 368;
const GUTTER = 24;
const colX = (i: number) => M + i * (COL_W + GUTTER);

const n = (v: number) => Math.round(v * 100) / 100;

let seq = 0;
/** An id nothing else in the deck uses, for the clip paths and filters. */
function uid(name: string): string {
  seq += 1;
  return `design-${name}-${seq}`;
}

const running = (page: number) =>
  text(M, 48, 13, 'Basket, checkout', { fill: C.muted, tracking: '0' }) +
  text(R, 48, 13, String(page).padStart(2, '0'), { fill: C.muted, anchor: 'end', tracking: '0' });

const headline = (rows: string[]) =>
  lines(M, HEAD_Y, 52, 60, rows, { font: DISPLAY, weight: 500, tracking: '-0.025em' });

/** A content slide: the ground, the running line, the headline, then the visual that carries it. */
const page = (num: number, head: string[], visual: string) =>
  wrap(rect(0, 0, W, H, C.ground) + running(num) + headline(head) + visual);

/** A soft shadow: a blurred copy of the shape, drawn under it. */
function softShadow(x: number, y: number, w: number, h: number, r: number, blur: number, opacity: number): string {
  const id = uid('shadow');
  return (
    `<filter id="${id}" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="${n(blur)}"/></filter>` +
    `<rect x="${n(x)}" y="${n(y)}" width="${n(w)}" height="${n(h)}" rx="${n(r)}" fill="${C.ink}" opacity="${opacity}" filter="url(#${id})"/>`
  );
}

/** A white card on the ground, edged with a hairline. */
const card = (x: number, y: number, w: number, h: number, r = 20) =>
  rect(x, y, w, h, C.white, r) + frame(x, y, w, h, C.hairline, r);

const pill = (x: number, y: number, w: number, label: string) =>
  rect(x, y, w, 28, C.green, 14) +
  text(n(x + w / 2), y + 19, 13, label, { fill: C.white, weight: 500, anchor: 'middle', tracking: '0' });

/* ---------------------------------------------------------------- phones */

/** Every screen is drawn at this logical width, then scaled into its phone. */
const LW = 300;
/** A screen in logical units, handed its logical height. */
type Screen = (lh: number) => string;

/** Type inside a phone: Inter, set the way a native screen sets it. */
const ui = (weight = 400, fill: string = C.ink, anchor: LinesOpts['anchor'] = 'start'): LinesOpts => ({
  font: SANS,
  weight,
  fill,
  anchor,
  tracking: '-0.01em',
});

function statusBar(): string {
  return (
    text(26, 31, 15, '9:41', ui(600)) +
    rect(221, 25, 3, 5, C.ink, 1) +
    rect(225.5, 23, 3, 7, C.ink, 1) +
    rect(230, 21, 3, 9, C.ink, 1) +
    rect(234.5, 19, 3, 11, C.ink, 1) +
    path('M 250 30 L 243.64 23.64 A 9 9 0 0 1 256.36 23.64 Z', C.ink) +
    `<rect x="262.5" y="19.5" width="22" height="11" rx="3.2" fill="none" stroke="${C.ink}" stroke-opacity="0.35"/>` +
    rect(264.5, 21.5, 15, 7, C.ink, 1.8) +
    rect(285.5, 23, 1.6, 4, C.ink, 0.8)
  );
}

const homeBar = (lh: number) => rect(LW / 2 - 60, n(lh - 9), 120, 4.5, C.ink, 2.25);

interface PhoneLook {
  body: string;
  glass: string;
  island: string;
  /** The side buttons; a wireframe has none. */
  side?: string;
  shadow: number;
  /** Status bar and home bar, which only a real screen has. */
  chrome: boolean;
}

const REAL: PhoneLook = { body: '#3A3A38', glass: '#0B0B0B', island: '#000000', side: '#2B2B29', shadow: 0.22, chrome: true };
const WIRE: PhoneLook = { body: '#E3E3DE', glass: '#E3E3DE', island: '#D3D3CE', shadow: 0, chrome: false };

/** A phone `w` wide at (x, y), its screen drawn by `screen` in logical units. */
function phone(x: number, y: number, w: number, screen: Screen, look: PhoneLook = REAL): string {
  const h = n(w * 2.05);
  const r = n(w * 0.16);
  const bezel = n(w * 0.036);
  const sx = n(x + bezel);
  const sy = n(y + bezel);
  const sw = n(w - bezel * 2);
  const sh = n(h - bezel * 2);
  const s = sw / LW;
  const lh = n(sh / s);
  const clip = uid('screen');
  const logical = (content: string) =>
    `<g transform="translate(${sx} ${sy}) scale(${Math.round(s * 10000) / 10000})">${content}</g>`;
  const buttons = look.side
    ? rect(n(x - 2), n(y + h * 0.2), 3, n(h * 0.05), look.side, 1.5) +
      rect(n(x - 2), n(y + h * 0.28), 3, n(h * 0.09), look.side, 1.5) +
      rect(n(x - 2), n(y + h * 0.39), 3, n(h * 0.09), look.side, 1.5) +
      rect(n(x + w - 1), n(y + h * 0.3), 3, n(h * 0.13), look.side, 1.5)
    : '';
  return (
    (look.shadow ? softShadow(x + w * 0.08, y + w * 0.14, w * 0.84, h * 0.94, r, w * 0.1, look.shadow) : '') +
    buttons +
    rect(x, y, w, h, look.body, r) +
    rect(x + 1.5, y + 1.5, w - 3, h - 3, look.glass, r - 1.5) +
    `<clipPath id="${clip}"><rect x="${sx}" y="${sy}" width="${sw}" height="${sh}" rx="${n(r - bezel)}"/></clipPath>` +
    `<g clip-path="url(#${clip})">${rect(sx, sy, sw, sh, C.white)}${logical((look.chrome ? statusBar() + homeBar(lh) : '') + screen(lh))}</g>` +
    logical(rect(LW / 2 - 45, 11, 90, 27, look.island, 13.5))
  );
}

/* ---------------------------------------------------------------- the basket */

interface Item {
  name: string;
  size: string;
  price: string;
  tile: string;
  /** The product, drawn in a 46 x 46 tile. */
  art: string;
}

const bean = (cx: number, cy: number, turn: number) =>
  `<g transform="rotate(${turn} ${cx} ${cy})"><ellipse cx="${cx}" cy="${cy}" rx="6.5" ry="9" fill="#6B4630"/>` +
  path(`M ${cx} ${cy - 7} C ${cx - 3} ${cy - 2.5} ${cx + 3} ${cy + 2.5} ${cx} ${cy + 7}`, 'none', '#EFE5D9', 1.5) +
  '</g>';

const BASKET: Item[] = [
  {
    name: 'Strawberries',
    size: '500 g',
    price: '€4.90',
    tile: '#FCE5E1',
    art:
      path('M 23 38 C 15.5 34 11.5 26.5 13.5 21 C 15.5 16.5 30.5 16.5 32.5 21 C 34.5 26.5 30.5 34 23 38 Z', '#E0483E') +
      path('M 14.5 18.5 C 17 14.5 20 15.5 23 13.5 C 26 15.5 29 14.5 31.5 18.5 C 28.5 19.5 26 18.5 23 20 C 20 18.5 17.5 19.5 14.5 18.5 Z', '#3E9A57') +
      [[19, 24], [27, 24], [23, 28.5], [19.5, 31.5], [26.5, 31.5]].map(([x, y]) => circle(x, y, 0.9, '#F7D591')).join(''),
  },
  {
    name: 'Avocados',
    size: '4 pack',
    price: '€5.70',
    tile: '#E6F0D8',
    art:
      path('M 23 8.5 C 28.5 8.5 31 14 32.5 21 C 34.5 30 31 38.5 23 38.5 C 15 38.5 11.5 30 13.5 21 C 15 14 17.5 8.5 23 8.5 Z', '#4A7A2B') +
      path('M 23 11.5 C 27 11.5 29 16 30.2 22 C 31.8 29.5 29 35.5 23 35.5 C 17 35.5 14.2 29.5 15.8 22 C 17 16 19 11.5 23 11.5 Z', '#D3E6A0') +
      circle(23, 27, 5.5, '#8A5A38'),
  },
  { name: 'Coffee beans', size: '1 kg', price: '€24.90', tile: '#F1E7DC', art: bean(18, 25, -28) + bean(29, 21, 22) },
];

const thumb = (x: number, y: number, size: number, item: Item) =>
  `<g transform="translate(${x} ${y}) scale(${n(size / 46)})">${rect(0, 0, 46, 46, item.tile, 12)}${item.art}</g>`;

const clock = (cx: number, cy: number) =>
  `<circle cx="${cx}" cy="${cy}" r="7.5" fill="none" stroke="${C.ink}" stroke-width="1.6"/>` +
  path(`M ${cx} ${cy - 4} L ${cx} ${cy} L ${cx + 3} ${cy + 2}`, 'none', C.ink, 1.6);

/** The shipped checkout: the basket, the fee in plain sight, one button with the whole price on it. */
const checkoutScreen: Screen = (lh) => {
  const rows = BASKET.map((item, i) => {
    const top = 104 + i * 60;
    return (
      thumb(20, top, 46, item) +
      text(78, top + 20, 15, item.name, ui(500)) +
      text(78, top + 39, 14, item.size, ui(400, C.muted)) +
      text(280, top + 20, 15, item.price, ui(500, C.ink, 'end'))
    );
  }).join('');
  const pay = n(lh - 84);
  return (
    path('M 29 62 L 22 69.5 L 29 77', 'none', C.ink, 2.2) +
    text(150, 75, 17, 'Checkout', ui(600, C.ink, 'middle')) +
    rows +
    line(20, 290, 280, 290, C.hairline) +
    text(20, 318, 15, 'Subtotal', ui(400, C.muted)) +
    text(280, 318, 15, '€35.50', ui(400, C.ink, 'end')) +
    rect(10, 332, 280, 40, C.lime, 10) +
    text(20, 357, 15, 'Delivery fee', ui(600)) +
    text(280, 357, 15, '€2.90', ui(600, C.ink, 'end')) +
    text(20, 404, 17, 'Total', ui(600)) +
    text(280, 404, 20, '€38.40', ui(600, C.ink, 'end')) +
    rect(20, 428, 260, 48, C.wash, 12) +
    clock(40, 452) +
    text(58, 457, 14, 'Delivery today, 18:40', ui(500)) +
    path('M 263 446 L 269 452 L 263 458', 'none', C.muted, 1.8) +
    rect(20, pay, 260, 50, C.green, 25) +
    text(150, pay + 31, 17, 'Pay €38.40', ui(600, C.white, 'middle'))
  );
};

/** The last screen says, in words, that it worked. */
const paidScreen: Screen = (lh) => {
  const top = 318;
  return (
    text(280, 75, 16, 'Done', ui(600, C.green, 'end')) +
    circle(150, 160, 34, C.green) +
    path('M 135.5 160 L 146 170.5 L 165.5 150', 'none', C.white, 5) +
    text(150, 232, 26, 'Paid', ui(600, C.ink, 'middle')) +
    text(150, 259, 16, 'Arriving 18:40', ui(400, C.muted, 'middle')) +
    rect(20, 284, 84, 5, C.green, 2.5) +
    rect(108, 284, 84, 5, C.hairline, 2.5) +
    rect(196, 284, 84, 5, C.hairline, 2.5) +
    rect(20, top, 260, 184, C.wash, 16) +
    BASKET.map((item, i) => thumb(36 + i * 40, top + 18, 34, item)).join('') +
    text(36, top + 90, 15, '3 items', ui(400, C.muted)) +
    text(264, top + 90, 15, '€35.50', ui(400, C.ink, 'end')) +
    text(36, top + 116, 15, 'Delivery fee', ui(400, C.muted)) +
    text(264, top + 116, 15, '€2.90', ui(400, C.ink, 'end')) +
    line(36, top + 132, 264, top + 132, '#E1E1DC') +
    text(36, top + 160, 15, 'Total paid', ui(600)) +
    text(264, top + 160, 15, '€38.40', ui(600, C.ink, 'end')) +
    rect(20, n(lh - 84), 260, 50, '#E5EFE9', 25) +
    text(150, n(lh - 53), 17, 'Track order', ui(600, C.green, 'middle'))
  );
};

/* ---------------------------------------------------------------- the wireframes */

const WF = { title: '#C6C6C0', block: '#E3E3DD', line: '#D8D8D2', soft: '#EEEEE9', strong: '#A2A29C' } as const;

const bar = (x: number, y: number, w: number, h: number, fill: string = WF.line) => rect(x, y, w, h, fill, h / 2);
const field = (y: number) => rect(20, y, 260, 34, WF.soft, 9);

const wireItems = (top: number) =>
  [0, 1, 2]
    .map((i) => {
      const y = top + i * 52;
      return rect(20, y, 40, 40, WF.block, 10) + bar(72, y + 8, 120, 9) + bar(72, y + 25, 70, 8, WF.soft) + bar(248, y + 8, 32, 9);
    })
    .join('');

/** A. Everything on one page: the fee sits somewhere below the fold. */
const longPage: Screen = () =>
  bar(20, 60, 120, 12, WF.title) +
  wireItems(90) +
  bar(20, 262, 90, 9, WF.title) +
  field(280) +
  field(322) +
  field(364) +
  bar(20, 418, 110, 9, WF.title) +
  rect(20, 436, 80, 30, WF.soft, 15) +
  rect(108, 436, 80, 30, WF.soft, 15) +
  rect(196, 436, 84, 30, WF.soft, 15) +
  bar(20, 490, 90, 9, WF.title) +
  field(508) +
  field(550) +
  bar(20, 606, 120, 9, WF.title) +
  field(624) +
  rect(291, 64, 3, 58, '#C9C9C4', 1.5);

/** B. Two steps: the basket with its whole price first, then address and payment. */
const twoSteps: Screen = () =>
  rect(20, 58, 127, 5, WF.strong, 2.5) +
  rect(153, 58, 127, 5, WF.block, 2.5) +
  bar(20, 82, 110, 12, WF.title) +
  wireItems(112) +
  line(20, 282, 280, 282, WF.block) +
  bar(20, 300, 70, 9) +
  bar(246, 300, 34, 9) +
  rect(12, 318, 276, 36, '#DDDDD7', 9) +
  bar(20, 331, 84, 10, WF.strong) +
  bar(242, 331, 38, 10, WF.strong) +
  bar(20, 376, 60, 12, WF.title) +
  bar(230, 376, 50, 12, WF.title) +
  rect(20, 410, 260, 48, WF.strong, 24);

/** C. The pay button first, the details under it. */
const payFirst: Screen = () =>
  bar(20, 60, 110, 12, WF.title) +
  rect(20, 88, 260, 50, WF.strong, 25) +
  bar(100, 156, 100, 8, WF.soft) +
  wireItems(180) +
  bar(20, 352, 90, 9, WF.title) +
  field(370) +
  field(412) +
  bar(20, 466, 90, 9, WF.title) +
  field(484) +
  bar(20, 540, 60, 8) +
  bar(240, 540, 40, 8) +
  bar(20, 558, 70, 8) +
  bar(244, 558, 36, 8);

/* ---------------------------------------------------------------- the slides */

function appIcon(x: number, y: number, size: number): string {
  return (
    `<g transform="translate(${x} ${y}) scale(${n(size / 64)})">` +
    rect(0, 0, 64, 64, C.green, 16) +
    path('M 23 28 C 23 17.5 41 17.5 41 28', 'none', C.white, 4) +
    path('M 13 28 H 51 L 46.6 46.5 C 46 49.2 43.9 51 41.2 51 H 22.8 C 20.1 51 18 49.2 17.4 46.5 Z', C.white) +
    '</g>'
  );
}

const cover = wrap(
  rect(0, 0, W, H, C.ground) +
    appIcon(M, 64, 44) +
    lines(M, 252, 96, 96, ['Basket', 'checkout'], { font: DISPLAY, weight: 500, tracking: '-0.035em' }) +
    text(M, 626, 15, 'A case study · 2026', { weight: 500, tracking: '-0.01em' }) +
    text(M, 650, 15, 'Guri Sitchinava, product designer', { fill: C.muted, tracking: '-0.01em' }) +
    phone(960, 64, 252, paidScreen) +
    phone(708, 96, 280, checkoutScreen),
);

const FUNNEL = [
  { label: 'Opened checkout', value: 100 },
  { label: 'Reached payment', value: 88 },
  { label: 'Paid', value: 58 },
];
const BAR_X = 304;
const barW = (value: number) => n(((R - BAR_X) * value) / 100);
const FUNNEL_TOP = 236;
const FUNNEL_PITCH = 120;

const problem = page(
  2,
  ['One in three people left at payment.'],
  FUNNEL.map(({ label, value }, i) => {
    const top = FUNNEL_TOP + i * FUNNEL_PITCH;
    return (
      text(M, top + 29, 40, String(value), { font: DISPLAY, weight: 500, tracking: '-0.02em' }) +
      text(M, top + 57, 15, label, { fill: C.muted, tracking: '-0.01em' }) +
      rect(BAR_X, top, barW(value) - (i === 2 ? 3 : 0), 64, C.bar, 8)
    );
  }).join('') +
    rect(BAR_X + barW(58) + 3, FUNNEL_TOP + 2 * FUNNEL_PITCH, barW(30) - 3, 64, C.coral, 8) +
    text(BAR_X + barW(58) + 23, FUNNEL_TOP + 2 * FUNNEL_PITCH + 38, 15, '30 left at payment', { weight: 500, tracking: '-0.01em' }) +
    text(M, 612, 13, 'Out of every 100 people, August', { fill: C.muted, tracking: '0' }),
);

const HEARD = [
  { initials: 'MK', name: 'Maya, 31', quote: ['“The fee only showed', 'up at the end.”'], count: 9 },
  { initials: 'TB', name: 'Tom, 45', quote: ['“It asked for my', 'address twice.”'], count: 7 },
  { initials: 'AR', name: 'Ana, 27', quote: ['“I could not tell', 'if it went through.”'], count: 5 },
];

const heard = page(
  3,
  ['Twelve interviews. One complaint.'],
  HEARD.map(({ initials, name, quote, count }, i) => {
    const x = colX(i);
    const y = VISUAL_Y;
    const h = 424;
    // The fee is the one complaint: its card leads, the other two stay quieter.
    const lead = i === 0;
    const dot = lead ? C.coral : '#BDBDB8';
    return (
      card(x, y, COL_W, h) +
      circle(x + 52, y + 56, 20, '#EDEDE8') +
      text(x + 52, y + 61, 13, initials, { weight: 600, anchor: 'middle', tracking: '0' }) +
      text(x + 84, y + 61, 15, name, { weight: 500, tracking: '-0.01em' }) +
      lines(x + 32, y + 184, 32, 40, quote, { font: DISPLAY, weight: 400, tracking: '-0.02em', fill: lead ? C.ink : C.muted }) +
      Array.from({ length: 12 }, (_, j) => circle(x + 37 + j * 18, y + h - 52, 5, j < count ? dot : C.hairline)).join('') +
      text(x + 262, y + h - 47, 15, `${count} of 12`, { weight: 500, tracking: '-0.01em' })
    );
  }).join(''),
);

const TRIES = [
  { label: 'A. One long page', screen: longPage, shipped: false },
  { label: 'B. Two steps', screen: twoSteps, shipped: true },
  { label: 'C. Pay first', screen: payFirst, shipped: false },
];

const tries = page(
  4,
  ['Three ways to shorten it.'],
  TRIES.map(({ label, screen, shipped }, i) => {
    const x = colX(i);
    const y = VISUAL_Y;
    const h = VISUAL_END - VISUAL_Y;
    const clip = uid('panel');
    return (
      rect(x, y, COL_W, h, C.white, 20) +
      `<clipPath id="${clip}"><rect x="${x}" y="${y}" width="${COL_W}" height="${h}" rx="20"/></clipPath>` +
      `<g clip-path="url(#${clip})">${phone(x + (COL_W - 236) / 2, y + 80, 236, screen, WIRE)}</g>` +
      frame(x, y, COL_W, h, shipped ? C.green : C.hairline, 20, shipped ? 1.5 : 1) +
      text(x + 28, y + 46, 16, label, { weight: 500, tracking: '-0.01em' }) +
      (shipped ? pill(x + COL_W - 28 - 82, y + 26, 82, 'Shipped') : '')
    );
  }).join(''),
);

/** The phones' tops sit on the headline's cap height. */
const shipped = page(5, ['Two steps.', 'The fee on the first.'], phone(652, 108, 262, checkoutScreen) + phone(954, 108, 262, paidScreen));

/** The same rows as the funnel, so the result reads as the problem answered. */
const RESULT = [
  { label: 'Before', value: 66, fill: '#C4C4BE', ink: C.muted },
  { label: 'After six weeks', value: 92, fill: C.green, ink: C.green },
];

const arrow = (x1: number, x2: number, y: number) =>
  path(`M ${x1} ${y} L ${x2} ${y} M ${x2 - 6} ${y - 6} L ${x2} ${y} L ${x2 - 6} ${y + 6}`, 'none', C.muted, 2);

const result = page(
  6,
  ['Paid went from 66% to 92%.'],
  RESULT.map(({ label, value, fill, ink }, i) => {
    const top = FUNNEL_TOP + i * FUNNEL_PITCH;
    return (
      text(M, top + 29, 40, `${value}%`, { font: DISPLAY, weight: 500, fill: ink, tracking: '-0.02em' }) +
      text(M, top + 57, 15, label, { fill: C.muted, tracking: '-0.01em' }) +
      rect(BAR_X, top, R - BAR_X, 64, C.track, 8) +
      rect(BAR_X, top, barW(value), 64, fill, 8)
    );
  }).join('') +
    line(M, 492, R, 492, C.hairline) +
    text(M, 552, 32, '40', { font: DISPLAY, weight: 500, fill: C.muted, tracking: '-0.02em' }) +
    arrow(115, 147, 541) +
    text(158, 552, 32, '3', { font: DISPLAY, weight: 500, fill: C.green, tracking: '-0.02em' }) +
    text(186, 552, 15, 'a week', { fill: C.muted, tracking: '-0.01em' }) +
    text(M, 580, 15, 'Fee questions to support', { fill: C.muted, tracking: '-0.01em' }),
);

const lesson = page(
  7,
  ['Show the whole price', 'before anything else.'],
  softShadow(684, 262, 520, 316, 28, 24, 0.1) +
    rect(672, 236, 544, 336, C.white, 28) +
    text(716, 314, 22, 'Subtotal', { fill: C.muted, tracking: '-0.01em' }) +
    text(1172, 314, 22, '€35.50', { anchor: 'end', tracking: '-0.01em' }) +
    rect(692, 344, 504, 68, C.lime, 16) +
    text(716, 386, 22, 'Delivery fee', { weight: 500, tracking: '-0.01em' }) +
    text(1172, 386, 22, '€2.90', { weight: 500, anchor: 'end', tracking: '-0.01em' }) +
    line(716, 446, 1172, 446, C.hairline) +
    text(716, 516, 24, 'Total', { weight: 500, tracking: '-0.01em' }) +
    text(1172, 520, 48, '€38.40', { font: DISPLAY, weight: 500, anchor: 'end', tracking: '-0.02em' }),
);

export const DESIGN_SLIDES: DemoSlide[] = [
  { id: 'design-1', title: 'Cover', svg: cover },
  { id: 'design-2', title: 'The problem', svg: problem },
  { id: 'design-3', title: 'What we heard', svg: heard },
  { id: 'design-4', title: 'Three tries', svg: tries },
  { id: 'design-5', title: 'What shipped', svg: shipped },
  { id: 'design-6', title: 'What it did', svg: result },
  { id: 'design-7', title: 'What I learned', svg: lesson },
];
